import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import Header from '../components/Header';
import Toast from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { formatCursoNombreAnio } from '../utils/cursoLabel';

interface Alumno {
  id: number;
  numeroAlumno: string;
  nombre: string;
  apellido: string;
  email: string;
  estado: string;
  inscripciones?: Array<{
    id: number;
    cursoId: number;
    turnoId: number | null;
    estado: string;
  }>;
}

interface Curso {
  id: number;
  nombre: string;
  codigo: string;
  descripcion: string;
  anio: number;
  cuatrimestre: number;
  turnos: Turno[];
}

interface Turno {
  id: number;
  nombre: string;
  dia: string;
  horaInicio: string;
  horaFin: string;
  cupoMaximo: number;
  cupoOcupado: number;
  activo: boolean;
}

const LandingPage: React.FC = () => {
  const [numeroAlumno, setNumeroAlumno] = useState('');
  const [alumno, setAlumno] = useState<Alumno | null>(null);
  const [selectedCurso, setSelectedCurso] = useState<Curso | null>(null);
  const [selectedTurno, setSelectedTurno] = useState<Turno | null>(null);
  const [step, setStep] = useState<'search' | 'select' | 'confirm'>('search');
  const [error, setError] = useState('');
  const { toast, showSuccess, showError, hideToast } = useToast();

  // Buscar alumno
  const buscarAlumnoMutation = useMutation({
    mutationFn: async (numero: string) => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/alumnos/buscar?numeroAlumno=${numero}`);
      return response.data;
    },
    onSuccess: (data) => {
      if (data.exists && data.alumno) {
        setAlumno(data.alumno);
        setError('');
        setStep('select');
      } else {
        setError('Alumno no encontrado. Verifica el número de legajo.');
        setAlumno(null);
      }
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || 'Error al buscar el alumno');
      setAlumno(null);
    }
  });

  // Obtener cursos activos
  const { data: cursos, isLoading: loadingCursos } = useQuery({
    queryKey: ['cursos-activos'],
    queryFn: async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/cursos/activos`);
      return response.data;
    },
    enabled: step === 'select'
  });

  // Crear inscripción
  const crearInscripcionMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/inscripciones`, data);
      return response.data;
    },
    onSuccess: () => {
      setStep('search');
      setNumeroAlumno('');
      setAlumno(null);
      setSelectedCurso(null);
      setSelectedTurno(null);
      setError('');
      showSuccess('Inscripción exitosa', 'Tu inscripción ha sido realizada correctamente');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Error al crear la inscripción';
      setError(errorMessage);
      showError('Error de inscripción', errorMessage);
    }
  });

  // Actualizar turno de inscripción existente
  const actualizarTurnoMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/inscripciones/actualizar-turno`, data);
      return response.data;
    },
    onSuccess: () => {
      setStep('search');
      setNumeroAlumno('');
      setAlumno(null);
      setSelectedCurso(null);
      setSelectedTurno(null);
      setError('');
      showSuccess('Turno actualizado', 'Tu turno ha sido actualizado correctamente');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Error al actualizar el turno';
      setError(errorMessage);
      showError('Error al actualizar turno', errorMessage);
    }
  });

  const handleBuscarAlumno = (e: React.FormEvent) => {
    e.preventDefault();
    if (numeroAlumno.trim()) {
      buscarAlumnoMutation.mutate(numeroAlumno.trim());
    }
  };

  const handleSeleccionarCurso = (curso: Curso) => {
    setSelectedCurso(curso);
    setSelectedTurno(null);
  };

  const handleSeleccionarTurno = (turno: Turno) => {
    setSelectedTurno(turno);
    setStep('confirm');
  };

  const handleConfirmarInscripcion = () => {
    if (alumno && selectedCurso && selectedTurno) {
      // Verificar si ya tiene una inscripción en este curso
      const inscripcionExistente = alumno.inscripciones?.find(
        (insc: any) => insc.cursoId === selectedCurso.id && insc.estado === 'activa'
      );

      if (inscripcionExistente) {
        // Si ya tiene inscripción, actualizar el turno
        if (inscripcionExistente.turnoId === selectedTurno.id) {
          showError('Ya estás inscrito en este turno');
          return;
        }
        
        // Actualizar turno de inscripción existente
        actualizarTurnoMutation.mutate({
          alumnoId: alumno.id,
          cursoId: selectedCurso.id,
          turnoId: selectedTurno.id
        });
      } else {
        // Crear nueva inscripción (alumno importado sin inscripción previa)
        crearInscripcionMutation.mutate({
          numeroAlumno: alumno.numeroAlumno,
          cursoId: selectedCurso.id,
          turnoId: selectedTurno.id
        });
      }
    }
  };

  const getDiaLabel = (dia: string) => {
    const dias = {
      'LUNES': 'Lunes',
      'MARTES': 'Martes',
      'MIERCOLES': 'Miércoles',
      'JUEVES': 'Jueves',
      'VIERNES': 'Viernes',
      'SABADO': 'Sábado',
      'DOMINGO': 'Domingo'
    };
    return dias[dia as keyof typeof dias] || dia;
  };

  const getCupoPercentage = (ocupado: number, maximo: number) => {
    return Math.round((ocupado / maximo) * 100);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Título principal */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Inscripción a Cursos
          </h1>
          <p className="text-lg text-gray-600">
            Facultad de Informática - Universidad Nacional de La Plata
          </p>
        </div>

        {/* Paso 1: Buscar alumno */}
        {step === 'search' && (
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-md mx-auto">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Buscar Alumno
              </h2>
              <p className="text-gray-600">
                Ingresa tu número de alumno para comenzar
              </p>
            </div>

            <form onSubmit={handleBuscarAlumno} className="space-y-4">
              <div>
                <label htmlFor="numeroAlumno" className="block text-sm font-medium text-gray-700 mb-2">
                  Número de Alumno
                </label>
                <input
                  type="text"
                  id="numeroAlumno"
                  value={numeroAlumno}
                  onChange={(e) => setNumeroAlumno(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Ej: 12345"
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={buscarAlumnoMutation.isPending}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                {buscarAlumnoMutation.isPending ? 'Buscando...' : 'Buscar Alumno'}
              </button>
            </form>
          </div>
        )}

        {/* Paso 2: Seleccionar curso y turno */}
        {step === 'select' && alumno && (
          <div className="space-y-6">
            {/* Información del alumno */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {alumno.nombre} {alumno.apellido}
                  </h3>
                  <p className="text-gray-600">Número: {alumno.numeroAlumno}</p>
                  <p className="text-gray-600">Email: {alumno.email}</p>
                </div>
                <button
                  onClick={() => setStep('search')}
                  className="ml-auto text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Lista de cursos */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Cursos Disponibles
              </h3>
              
              {loadingCursos ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Cargando cursos...</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {cursos?.map((curso: Curso) => (
                    <div
                      key={curso.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        selectedCurso?.id === curso.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handleSeleccionarCurso(curso)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-gray-900">{formatCursoNombreAnio(curso)}</h4>
                          <p className="text-sm text-gray-600">Código: {curso.codigo}</p>
                          <p className="text-sm text-gray-600">
                            {curso.anio} - {curso.cuatrimestre}° Cuatrimestre
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="text-sm text-gray-500">
                            {curso.turnos?.length || 0} turnos disponibles
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Turnos del curso seleccionado */}
            {selectedCurso && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Turnos - {formatCursoNombreAnio(selectedCurso)}
                </h3>
                
                <div className="grid gap-4">
                  {selectedCurso.turnos?.filter(turno => turno.activo).map((turno) => (
                    <div
                      key={turno.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        selectedTurno?.id === turno.id
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handleSeleccionarTurno(turno)}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-semibold text-gray-900">{turno.nombre}</h4>
                          <p className="text-sm text-gray-600">
                            {getDiaLabel(turno.dia)} {turno.horaInicio} - {turno.horaFin}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">
                            {turno.cupoOcupado}/{turno.cupoMaximo} cupos
                          </div>
                          <div className="w-20 bg-gray-200 rounded-full h-2 mt-1">
                            <div
                              className={`h-2 rounded-full ${
                                getCupoPercentage(turno.cupoOcupado, turno.cupoMaximo) > 80
                                  ? 'bg-red-500'
                                  : getCupoPercentage(turno.cupoOcupado, turno.cupoMaximo) > 60
                                  ? 'bg-yellow-500'
                                  : 'bg-green-500'
                              }`}
                              style={{ width: `${getCupoPercentage(turno.cupoOcupado, turno.cupoMaximo)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Paso 3: Confirmar inscripción */}
        {step === 'confirm' && alumno && selectedCurso && selectedTurno && (
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-md mx-auto">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Confirmar Inscripción
              </h2>
              <p className="text-gray-600">
                Revisa los datos antes de confirmar
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Alumno</h3>
                <p className="text-gray-600">{alumno.nombre} {alumno.apellido}</p>
                <p className="text-sm text-gray-500">Número: {alumno.numeroAlumno}</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Curso</h3>
                <p className="text-gray-600">{formatCursoNombreAnio(selectedCurso)}</p>
                <p className="text-sm text-gray-500">Código: {selectedCurso.codigo}</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Turno</h3>
                <p className="text-gray-600">{selectedTurno.nombre}</p>
                <p className="text-sm text-gray-500">
                  {getDiaLabel(selectedTurno.dia)} {selectedTurno.horaInicio} - {selectedTurno.horaFin}
                </p>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={() => setStep('select')}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                Volver
              </button>
              <button
                onClick={handleConfirmarInscripcion}
                disabled={crearInscripcionMutation.isPending}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                {crearInscripcionMutation.isPending ? 'Confirmando...' : 'Confirmar Inscripción'}
              </button>
            </div>
          </div>
        )}
      </main>
      
      {/* Toast notifications */}
      <Toast
        type={toast.type}
        title={toast.title}
        message={toast.message}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </div>
  );
};

export default LandingPage;
